import type { EmergencyNumber, Language, ProfessionalCategory, StreamStage } from '@/types/chat';

export const PROFESSIONAL_LABELS: Record<ProfessionalCategory, Record<Language, string>> = {
  medecin_traitant: {
    fr: 'Médecin traitant',
    en: 'Family doctor',
    zh: '家庭医生',
    es: 'Médico de cabecera',
  },
  gynecologue: {
    fr: 'Gynécologue',
    en: 'Gynecologist',
    zh: '妇科医生',
    es: 'Ginecólogo·a',
  },
  sage_femme: {
    fr: 'Sage-femme',
    en: 'Midwife',
    zh: '助产士',
    es: 'Matrona',
  },
  pediatre: {
    fr: 'Pédiatre',
    en: 'Pediatrician',
    zh: '儿科医生',
    es: 'Pediatra',
  },
  pharmacien: {
    fr: 'Pharmacien·ne',
    en: 'Pharmacist',
    zh: '药剂师',
    es: 'Farmacéutico·a',
  },
  specialiste: {
    fr: 'Spécialiste médical',
    en: 'Medical specialist',
    zh: '专科医生',
    es: 'Especialista médico',
  },
  mtc_praticien: {
    fr: 'Praticien MTC',
    en: 'TCM practitioner',
    zh: '中医师',
    es: 'Practicante de MTC',
  },
};

export const EMERGENCY_LABELS: Record<EmergencyNumber, Record<Language, string>> = {
  samu_15: {
    fr: 'Le 15 (SAMU)',
    en: 'Dial 15 (SAMU)',
    zh: '拨打 15',
    es: 'Marque el 15',
  },
  europe_112: {
    fr: 'Le 112 (urgence européen)',
    en: 'Dial 112 (European emergency)',
    zh: '拨打 112',
    es: 'Marque el 112',
  },
  us_911: {
    fr: 'Le 911 (urgence USA)',
    en: 'Dial 911',
    zh: '拨打 911',
    es: 'Marque el 911',
  },
  uk_999: {
    fr: 'Le 999 (UK)',
    en: 'Dial 999',
    zh: '拨打 999',
    es: 'Marque el 999',
  },
  china_120: {
    fr: 'Le 120 (urgence Chine)',
    en: 'Dial 120',
    zh: '拨打 120',
    es: 'Marque el 120',
  },
};

export const EMERGENCY_TEL: Record<EmergencyNumber, string> = {
  samu_15: 'tel:15',
  europe_112: 'tel:112',
  us_911: 'tel:911',
  uk_999: 'tel:999',
  china_120: 'tel:120',
};

/**
 * Common UI strings used by all three widgets. Add keys as needed during
 * Phases 2 / 3 — keep keys in alphabetical order per locale.
 */
export const UI_LABELS: Record<string, Record<Language, string>> = {
  session_loading: {
    fr: 'Chargement de votre conversation…',
    en: 'Loading your conversation…',
    zh: '正在加载您的对话…',
    es: 'Cargando tu conversación…',
  },
  greeting_title: {
    fr: 'Bonjour !',
    en: 'Hello!',
    zh: '您好！',
    es: '¡Hola!',
  },
  greeting_body: {
    fr: 'Je suis **Flo(阿草)**, votre assistant virtuel.\nComment puis-je vous aider aujourd’hui ?',
    en: 'I’m **Flo(阿草)**, your virtual assistant.\nHow can I help you today?',
    zh: '我是 **Flo(阿草)**，您的虚拟助手。\n今天有什么可以帮您？',
    es: 'Soy **Flo(阿草)**, su asistente virtual.\n¿En qué puedo ayudarle hoy?',
  },
  floating_button_label: {
    fr: 'Conseiller virtuel',
    en: 'Virtual advisor',
    zh: '虚拟顾问',
    es: 'Asesor virtual',
  },
  blog_suggestions_title: {
    fr: 'Vous pourriez aussi aimer',
    en: 'You might also like',
    zh: '你可能感兴趣',
    es: 'También te podría interesar',
  },
  related_pages_title: {
    fr: 'Pages utiles',
    en: 'Useful pages',
    zh: '相关页面',
    es: 'Páginas útiles',
  },
  product_about: {
    fr: 'Pourquoi ce produit',
    en: 'Why this product',
    zh: '为什么推荐这款产品',
    es: 'Por qué este producto',
  },
  nomatch_question: {
    fr: 'Vous n’avez pas trouvé ce que vous cherchiez pour « {terme} » ?',
    en: 'Didn’t find what you were looking for about “{terme}” ?',
    zh: '没有找到关于“{terme}”想要的内容？',
    es: '¿No encontraste lo que buscabas sobre «{terme}»?',
  },
  nomatch_cta: {
    fr: 'Demandez conseil à notre assistant virtuel',
    en: 'Ask our virtual assistant',
    zh: '向我们的虚拟助手咨询',
    es: 'Pregunta a nuestro asistente virtual',
  },
  blog_prev: {
    fr: 'Précédent',
    en: 'Previous',
    zh: '上一篇',
    es: 'Anterior',
  },
  blog_next: {
    fr: 'Suivant',
    en: 'Next',
    zh: '下一篇',
    es: 'Siguiente',
  },
  feedback_helpful: {
    fr: 'Réponse utile',
    en: 'Helpful response',
    zh: '回答有帮助',
    es: 'Respuesta útil',
  },
  feedback_not_helpful: {
    fr: 'Réponse peu utile',
    en: 'Not helpful',
    zh: '回答没帮助',
    es: 'Poco útil',
  },
  feedback_dialog_title: {
    fr: 'Qu’est-ce qui n’a pas fonctionné ?',
    en: 'What went wrong?',
    zh: '哪里不对？',
    es: '¿Qué no funcionó?',
  },
  feedback_comment_placeholder: {
    fr: 'Ajouter un commentaire (facultatif)',
    en: 'Add a comment (optional)',
    zh: '补充说明（可选）',
    es: 'Añadir un comentario (opcional)',
  },
  feedback_comment_required: {
    fr: 'Un commentaire est requis pour cette raison.',
    en: 'A comment is required for this reason.',
    zh: '选择该原因需要填写说明。',
    es: 'Se requiere un comentario para este motivo.',
  },
  feedback_submit: {
    fr: 'Envoyer',
    en: 'Submit',
    zh: '提交',
    es: 'Enviar',
  },
  feedback_submit_error: {
    fr: 'Impossible d’envoyer le retour. Réessayez.',
    en: 'Could not submit feedback. Please retry.',
    zh: '提交失败，请重试。',
    es: 'No se pudo enviar. Vuelve a intentarlo.',
  },
  feedback_close: {
    fr: 'Fermer',
    en: 'Close',
    zh: '关闭',
    es: 'Cerrar',
  },
  retry: {
    fr: 'Réessayer',
    en: 'Retry',
    zh: '重试',
    es: 'Reintentar',
  },
  retrying: {
    fr: 'Nouvel essai…',
    en: 'Retrying…',
    zh: '正在重试…',
    es: 'Reintentando…',
  },
  history_load_failed: {
    fr: 'Impossible de charger la conversation.',
    en: 'Could not load the conversation.',
    zh: '无法加载对话记录。',
    es: 'No se pudo cargar la conversación.',
  },
};

export function getLabel<K extends keyof typeof UI_LABELS>(key: K, lang: Language): string {
  return UI_LABELS[key][lang] ?? UI_LABELS[key].fr;
}

/**
 * Cycling status messages shown in the streaming bubble while the assistant
 * is generating a response. Cycle interval is ~2.5s; total cycle ~20s,
 * which covers most response times.
 */
export const STREAMING_STATUS_MESSAGES: Record<Language, string[]> = {
  fr: [
    "L'assistant réfléchit à votre question…",
    'Analyse de votre demande…',
    'Recherche dans le catalogue Calebasse…',
    'Consultation des principes de la MTC…',
    'Sélection des produits adaptés…',
    'Préparation des recommandations…',
    'Vérification des informations…',
    'Finalisation de la réponse…',
  ],
  en: [
    'The assistant is thinking about your question…',
    'Analyzing your request…',
    'Searching the Calebasse catalog…',
    'Consulting TCM principles…',
    'Selecting suitable products…',
    'Preparing recommendations…',
    'Verifying information…',
    'Finalizing the response…',
  ],
  zh: [
    '助手正在思考您的问题…',
    '分析您的需求…',
    '搜索 Calebasse 产品目录…',
    '查阅中医理论…',
    '挑选合适的产品…',
    '准备建议…',
    '核对信息…',
    '完善回复…',
  ],
  es: [
    'El asistente está pensando en su pregunta…',
    'Analizando su solicitud…',
    'Buscando en el catálogo Calebasse…',
    'Consultando los principios de la MTC…',
    'Seleccionando productos adecuados…',
    'Preparando recomendaciones…',
    'Verificando la información…',
    'Finalizando la respuesta…',
  ],
};

/**
 * Map backend error codes (from `error.code` in 4xx/5xx responses and
 * SSE error events) to localized user-friendly text.
 *
 * Codes per docs/frontend_streaming_spec.md §6.2.
 */
/** Where the backend is at, from a stream's status events: understanding, searching, writing. */
export const STREAM_STAGE_LABELS: Record<Language, Record<StreamStage, string>> = {
  fr: {
    analyse: 'Analyse de votre demande…',
    recherche: 'Recherche dans le catalogue Calebasse…',
    redaction: 'Rédaction de la réponse…',
  },
  en: {
    analyse: 'Analysing your question…',
    recherche: 'Searching the Calebasse catalogue…',
    redaction: 'Writing the answer…',
  },
  zh: {
    analyse: '正在理解您的问题…',
    recherche: '正在检索 Calebasse 产品目录…',
    redaction: '正在撰写回答…',
  },
  es: {
    analyse: 'Analizando su pregunta…',
    recherche: 'Buscando en el catálogo Calebasse…',
    redaction: 'Redactando la respuesta…',
  },
};

export const ERROR_MESSAGES: Record<string, Record<Language, string>> = {
  INVALID_REQUEST: {
    fr: 'Votre message n’a pas pu être envoyé. Veuillez le reformuler.',
    en: 'Your message could not be sent. Please rephrase it.',
    zh: '您的消息无法发送。请重新描述。',
    es: 'No se pudo enviar su mensaje. Por favor, reformule.',
  },
  UNAUTHORIZED: {
    fr: 'Session expirée. Veuillez recharger la page.',
    en: 'Session expired. Please reload the page.',
    zh: '会话已过期。请重新加载页面。',
    es: 'Sesión expirada. Por favor, recargue la página.',
  },
  RATE_LIMITED: {
    fr: 'Trop de requêtes. Veuillez patienter quelques secondes.',
    en: 'Too many requests. Please wait a few seconds.',
    zh: '请求过多。请稍等几秒。',
    es: 'Demasiadas solicitudes. Por favor, espere unos segundos.',
  },
  SESSION_NOT_FOUND: {
    fr: 'Conversation introuvable. Une nouvelle session va démarrer.',
    en: 'Conversation not found. A new session will start.',
    zh: '未找到会话。将开始新会话。',
    es: 'Conversación no encontrada. Se iniciará una nueva sesión.',
  },
  INTENT_DETECTION_FAILED: {
    fr: 'Une erreur est survenue. Veuillez réessayer.',
    en: 'Something went wrong. Please try again.',
    zh: '出现错误。请重试。',
    es: 'Algo salió mal. Por favor, intente de nuevo.',
  },
  EXTERNAL_PROVIDER_TIMEOUT: {
    fr: 'Notre service met du temps à répondre. Veuillez réessayer.',
    en: 'Our service is slow to respond. Please try again.',
    zh: '服务响应较慢。请重试。',
    es: 'Nuestro servicio está lento. Por favor, intente de nuevo.',
  },
  RESPONSE_GENERATION_FAILED: {
    fr: 'L’assistant n’a pas pu répondre. Veuillez réessayer.',
    en: 'The assistant could not respond. Please try again.',
    zh: '助手未能回复。请重试。',
    es: 'El asistente no pudo responder. Por favor, intente de nuevo.',
  },
  INTERNAL_ERROR: {
    fr: 'Une erreur interne est survenue. Veuillez réessayer.',
    en: 'An internal error occurred. Please try again.',
    zh: '发生内部错误。请重试。',
    es: 'Ocurrió un error interno. Por favor, intente de nuevo.',
  },
  NETWORK_ERROR: {
    fr: 'Problème de connexion. Vérifiez votre réseau et réessayez.',
    en: 'Connection problem. Check your network and try again.',
    zh: '连接问题。请检查网络后重试。',
    es: 'Problema de conexión. Verifique su red e intente de nuevo.',
  },
};

/**
 * Look up a friendly error message for an error. Falls back to the raw
 * code (then to a generic message) if the code isn't mapped.
 */
export function getFriendlyErrorMessage(
  code: string | undefined,
  errorName: string | undefined,
  lang: Language,
): string {
  const lookupCode = code ?? (errorName === 'NetworkError' ? 'NETWORK_ERROR' : 'INTERNAL_ERROR');
  const entry = ERROR_MESSAGES[lookupCode];
  if (entry) return entry[lang] ?? entry.fr;
  return ERROR_MESSAGES.INTERNAL_ERROR[lang] ?? ERROR_MESSAGES.INTERNAL_ERROR.fr;
}

/**
 * The fixed welcome message shown as the first bubble in the chat widget
 * regardless of entry path (overview CTA or floating button).
 *
 * V1: French only — en/zh/es deferred per Pierre's 2026-05-12 decision.
 *
 * The text contains markdown (bold + a CGU link). The chat widget's
 * markdown renderer will handle it; Phase 1 just stores it as a string.
 */
export const WELCOME_MESSAGE_FR = `Je peux vous aider à :

- Découvrir la culture et les principes de la médecine traditionnelle chinoise
- Explorer notre catalogue et trouver des produits adaptés à vos besoins de bien-être
- Vous apporter des conseils de bien-être basés sur la MTC
- Vous informer sur nos politiques de commande et livraison (pour le suivi de commande, connectez-vous à votre espace client)

⚠️ Limites importantes :

Je suis un assistant de navigation, pas un professionnel de santé. Mes recommandations sont proposées à des fins de **bien-être général** et ne constituent en aucun cas un diagnostic ou un traitement médical.

En cas de problème de santé, consultez toujours un médecin ou un praticien qualifié.

📜 En utilisant ce service, vous acceptez nos [conditions générales d'utilisation](https://calebasse.com/cgu)`;

/**
 * Get the welcome message for a given locale. V1 always returns the fr
 * version (en/zh/es to be added later).
 */
export function getWelcomeMessage(_lang: Language): string {
  return WELCOME_MESSAGE_FR;
}
